import { PasswordInput as DojoPasswordInput } from '@casumo/dojo-react';
import {
  ChangeEvent,
  KeyboardEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ValidationRuleItem } from './validationRuleItem.tsx';
import { useRegistrationStore } from '../../store/store.ts';
import { TPasswordInputField } from '../../types/types.ts';
import { TEST_ID } from '../../utils/constants.ts';
import { GTM_ERRORS } from '../../utils/gtm/constants.ts';
import { GtmEvents } from '../../utils/gtm/gtm.ts';
import validate, { isPasswordValid } from '../../utils/passwordValidation.ts';

const PasswordInput = ({
  name,
  value,
  label,
  ruleMinLength,
  ruleHasLetter,
  ruleHasNumber,
  ruleHasSpecialChar,
  successMessage,
  errorMessage,
  warningNote,
  validationPanelTitle,
  onKeyDown,
}: TPasswordInputField & {
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  value?: string;
}) => {
  const {
    errors,
    setErrors,
    formData,
    setFormData,
    customValidations,
    setCustomValidations,
    metadata,
  } = useRegistrationStore();

  const [isFocused, setIsFocused] = useState(false);
  const [hasBlurred, setHasBlurred] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // DojoPasswordInput overrides onFocus for its show/hide mask, so we track focus
  // via a wrapper using bubbling focusin/focusout.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const handleFocusIn = () => setIsFocused(true);
    const handleFocusOut = (e: FocusEvent) => {
      if (!el.contains(e.relatedTarget as Node)) {
        setIsFocused(false);
      }
    };
    el.addEventListener('focusin', handleFocusIn);
    el.addEventListener('focusout', handleFocusOut);

    return () => {
      el.removeEventListener('focusin', handleFocusIn);
      el.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  const password = useMemo(
    () => value || formData?.[name] || '',
    [value, formData, name],
  );

  const ruleLabels = useMemo(
    () => ({
      hasLetter:      ruleHasLetter || '',
      hasNumber:      ruleHasNumber || '',
      hasSpecialChar: ruleHasSpecialChar || '',
      minLength:      ruleMinLength || '',
    }),
    [ruleHasLetter, ruleHasNumber, ruleHasSpecialChar, ruleMinLength],
  );

  const validationResult = useMemo(
    () => validate(password, ruleLabels),
    [password, ruleLabels],
  );

  const handleBlur = useCallback((e: { relatedTarget: EventTarget | null }) => {
    // If focus is moving to another element within the wrapper (e.g. show/hide button), skip blur logic
    if (wrapperRef.current?.contains(e.relatedTarget as Node)) {
      return;
    }

    setHasBlurred(true);

    if (!password) {
      setCustomValidations({
        ...customValidations,
        [name]: errorMessage || '',
      });

      GtmEvents.logAlert({
        alert: GTM_ERRORS.INVALID_PASSWORD,
        step:  metadata?.currentStep,
      });

      return;
    }

    if (!isPasswordValid(password)) {
      setCustomValidations({
        ...customValidations,
        [name]: errorMessage || '',
      });

      GtmEvents.logAlert({
        alert: GTM_ERRORS.INVALID_PASSWORD,
        step:  metadata?.currentStep,
      });

      return;
    }

    setCustomValidations({
      ...customValidations,
      [name]: '',
    });
  }, [
    password, name, errorMessage, metadata?.currentStep,
    customValidations, setCustomValidations,
  ]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.currentTarget.value;

      setErrors({ ...errors, [name]: '' });
      setCustomValidations({ ...customValidations, [name]: '' });
      setFormData({ [name]: newValue });
    },
    [errors, customValidations, name, setErrors, setCustomValidations, setFormData],
  );

  const hasExternalError = !!(errors[name] || customValidations[name]);
  const showPostBlurMessage = !isFocused && (hasBlurred || hasExternalError);

  return (
    <div ref={wrapperRef} className="w-full flex flex-col gap-2xs font-circular-pro">
      <DojoPasswordInput
        id="passwordInput"
        autoComplete="new-password"
        name={name}
        label={label}
        value={password}
        error={!!(errors[name] || customValidations[name])}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
        data-testid={TEST_ID.INPUT + 'plainTextPassword'}
      />

      {isFocused && (
        <div
          className="p-xs rounded-lg text-sm animate-fadeIn transition-colors bg-grey-0"
          data-testid={TEST_ID.PREFIX + 'password-validation-panel'}
        >
          <div className="flex flex-col gap-xs">
            {validationPanelTitle && (
              <p className="text-grey-70 text-body-2xs">
                {validationPanelTitle}
              </p>
            )}
            <ul className="space-y-1.5">
              {validationResult.rules.map((rule) => (
                <ValidationRuleItem key={rule.id} rule={rule} />
              ))}
            </ul>
            {warningNote && (
              <p className="text-grey-70 text-body-2xs" dangerouslySetInnerHTML={{ 
                __html: warningNote }}>
              </p>
            )}
          </div>
        </div>
      )}

      {showPostBlurMessage && (
        <>  
          {validationResult.isAllValid ? (
            successMessage && (
              <div className="text-body-xs text-green-30">
                {successMessage}
              </div>
            )
          ) : (
            errorMessage && (
              <div className="text-red-30 text-body-xs">
                {errorMessage}
              </div>
            )
          )}
        </>
      )}
    </div>
  );
};

const Memoized = memo(PasswordInput);
Memoized.displayName = 'PasswordInput';

export default Memoized;
